import {
  resourceInfo,
  resourceInfoLayout,
} from '@squareup/dex-ui-shared-markdown-components';
import React, { FC, isValidElement, PropsWithChildren, ReactNode } from 'react';

interface MdxResourceInfoProps {
  iconPath: string;
  iconAlt: string;
  title?: string;
}

const ResourceInfoComponent = resourceInfo.component.value as FC<
  PropsWithChildren<MdxResourceInfoProps>
>;
const ResourceInfoLayoutComponent = resourceInfoLayout.component
  .value as FC<PropsWithChildren>;

function isParagraphWrapper(type: unknown): boolean {
  if (type === 'p') {
    return true;
  }

  if (typeof type === 'function') {
    return (type as FC).displayName === 'MdxParagraph';
  }

  return false;
}

function unwrapMdxParagraph(node: ReactNode): ReactNode {
  if (Array.isArray(node)) {
    return node.map(unwrapMdxParagraph);
  }

  if (!isValidElement(node)) {
    return node;
  }

  // MDX wraps inline link runs in <p> / MdxParagraph even inside JSX children.
  // DocResourceInfo already renders body copy in Paragraph30 (<p>), so unwrap
  // to avoid invalid nested paragraphs and the resulting hydration mismatch.
  if (isParagraphWrapper(node.type)) {
    return unwrapMdxParagraph(node.props.children);
  }

  if (node.type === 'span' && isValidElement(node.props.children)) {
    const inner = unwrapMdxParagraph(node.props.children);
    if (inner !== node.props.children) {
      return <span>{inner}</span>;
    }
  }

  return node;
}

const MdxResourceInfo: FC<PropsWithChildren<MdxResourceInfoProps>> = ({
  children,
  ...props
}) => {
  return (
    <ResourceInfoComponent {...props}>
      {unwrapMdxParagraph(children)}
    </ResourceInfoComponent>
  );
};

MdxResourceInfo.displayName = 'MdxResourceInfo';

const MdxResourceInfoLayout: FC<PropsWithChildren> = ({ children }) => {
  return <ResourceInfoLayoutComponent>{children}</ResourceInfoLayoutComponent>;
};

MdxResourceInfoLayout.displayName = 'MdxResourceInfoLayout';

export { MdxResourceInfo, MdxResourceInfoLayout };
